/** @jsx jsx */
import { useMemo, useState } from 'react';
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, Grid, TablePagination } from '@material-ui/core';
import { orderBy } from 'lodash';

import useBoolean from 'core/utils/use-boolean.hook';
import { ICustomTheme, IChallengeListProductItem } from 'interfaces';
import { SortToggle, TablePaginationActions } from 'components';
import Product from './Product';
import * as S from './styles';

interface IProductList {
  exportUrl: string;
  products: IChallengeListProductItem[];
  getProductTitle: (product: IChallengeListProductItem) => string;
  rowsPerPage?: number;
  isDisabled?: boolean;
}

export default function ProductsList({
  exportUrl,
  products,
  getProductTitle,
  rowsPerPage,
  isDisabled,
}: IProductList) {
  const { t } = useTranslation();
  const { value: isAsc, toggle: toggleIsAsc } = useBoolean(true);
  const isDownSm = useMediaQuery((theme: ICustomTheme) =>
    theme.breakpoints.down('sm'),
  );

  const [page, setPage] = useState(0);
  const isPaginationEnabled = !!rowsPerPage && products.length > rowsPerPage;
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const stringProducts = JSON.stringify(products);
  const displayedProducts = useMemo(() => {
    const order = isAsc ? 'asc' : 'desc';
    const sortedProducts = orderBy(
      products,
      [
        (product) => product.reward ?? product.coefficient,
        (product) => product.challengeProduct.productName.toLowerCase(),
      ],
      [order, order],
    );

    return !!rowsPerPage && isPaginationEnabled
      ? sortedProducts.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage,
        )
      : sortedProducts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationEnabled, stringProducts, isAsc, page, rowsPerPage]);

  return (
    <S.Container>
      <S.ToolBar>
        <S.SortContainer>
          <SortToggle
            isDisabled={isDisabled}
            isAsc={isAsc}
            toggleIsAsc={toggleIsAsc}
            ascLabel={t('challenges.detail.productsAndGain.ascendingGains')}
            descLabel={t('challenges.detail.productsAndGain.descendingGains')}
          />
        </S.SortContainer>
        <div>
          <S.ExportButton
            target="_blank"
            rel="noopener noreferrer"
            color={isDisabled ? 'infoInverted' : 'primaryInverted'}
            href={exportUrl}
            download
          >
            <S.ExportIcon color={isDisabled ? 'secondary' : 'primary'} />
            {isDownSm
              ? t('challenges.detail.productsAndGain.export')
              : t('challenges.detail.productsAndGain.exportProductsList')}
          </S.ExportButton>
        </div>
      </S.ToolBar>
      <Grid
        container
        justifyContent="center"
        spacing={4}
        css={S.cssGridContainer}
      >
        {displayedProducts.map((product) => (
          <Grid key={product.challengeProduct.id} item css={S.cssGridItem}>
            <Product
              product={product}
              getProductTitle={getProductTitle}
              isDisabled={isDisabled}
            />
          </Grid>
        ))}
      </Grid>
      {isPaginationEnabled && (
        <TablePagination
          component="div"
          rowsPerPageOptions={[]}
          page={page}
          count={products.length}
          rowsPerPage={rowsPerPage!}
          onPageChange={handleChangePage}
          ActionsComponent={TablePaginationActions}
        />
      )}
    </S.Container>
  );
}
